import React, { useEffect, useMemo, useState } from "react";
import { FaRegClock, FaTimes, FaRegLightbulb } from "react-icons/fa";
import "./TimePickerModal.css";

const PERIODS = [
  { id: "morning", label: "Morning", range: "6 AM – 12 PM", startHour: 6, endHour: 12 },
  { id: "afternoon", label: "Afternoon", range: "12 PM – 6 PM", startHour: 12, endHour: 18 },
  { id: "evening", label: "Evening", range: "6 PM – 12 AM", startHour: 18, endHour: 24 },
  { id: "night", label: "Night", range: "12 AM – 6 AM", startHour: 0, endHour: 6 },
];

const formatHourLabel = (hour) => {
  const normalized = ((hour % 24) + 24) % 24;
  const period = normalized >= 12 ? "PM" : "AM";
  const display = normalized % 12 === 0 ? 12 : normalized % 12;
  return `${String(display).padStart(2, "0")}:00 ${period}`;
};

const toTimeString = (hour) => {
  const normalized = ((hour % 24) + 24) % 24;
  return `${String(normalized).padStart(2, "0")}:00`;
};

const periodForTime = (time) => {
  if (!time) return "morning";
  const [hStr] = time.split(":");
  const h = Number(hStr);
  if (!Number.isFinite(h)) return "morning";
  const found = PERIODS.find((p) => h >= p.startHour && h < p.endHour);
  return found ? found.id : "morning";
};

const TimePickerModal = ({
  open,
  title = "Select time",
  initialTime = "",
  helperText,
  onClose,
  onConfirm,
}) => {
  const [activePeriod, setActivePeriod] = useState(periodForTime(initialTime));
  const [selectedTime, setSelectedTime] = useState(initialTime || "");

  useEffect(() => {
    if (open) {
      setSelectedTime(initialTime || "");
      setActivePeriod(periodForTime(initialTime));
    }
  }, [open, initialTime]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const slots = useMemo(() => {
    const period = PERIODS.find((p) => p.id === activePeriod) || PERIODS[0];
    const result = [];
    for (let h = period.startHour; h < period.endHour; h += 1) {
      result.push(h);
    }
    return result;
  }, [activePeriod]);

  if (!open) return null;

  const handleConfirm = () => {
    if (!selectedTime) {
      onClose?.();
      return;
    }
    onConfirm?.(selectedTime);
  };

  return (
    <div
      className="time-picker-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="time-picker-modal">
        <header className="tpm-header">
          <div className="tpm-title">
            <span className="tpm-title-icon" aria-hidden>
              <FaRegClock />
            </span>
            <h2>{title}</h2>
          </div>
          <button
            type="button"
            className="tpm-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <div className="tpm-periods">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`tpm-period-pill ${activePeriod === p.id ? "active" : ""}`}
              onClick={() => setActivePeriod(p.id)}
            >
              <span className="tpm-period-label">{p.label}</span>
              <span className="tpm-period-range">{p.range}</span>
            </button>
          ))}
        </div>

        <div className="tpm-slots-grid">
          {slots.map((hour) => {
            const value = toTimeString(hour);
            const isSelected = value === selectedTime;
            return (
              <button
                key={value}
                type="button"
                className={`tpm-slot ${isSelected ? "selected" : ""}`}
                onClick={() => setSelectedTime(value)}
              >
                {formatHourLabel(hour)}
              </button>
            );
          })}
        </div>

        {helperText && (
          <p className="tpm-helper">
            <FaRegLightbulb aria-hidden />
            <span>{helperText}</span>
          </p>
        )}

        <button
          type="button"
          className="tpm-done-btn"
          onClick={handleConfirm}
          disabled={!selectedTime}
        >
          Done
        </button>

        <p className="tpm-footnote">
          <FaRegClock aria-hidden /> <span>Time shown in local time</span>
        </p>
      </div>
    </div>
  );
};

export default TimePickerModal;
